/**
 * Module 'project-protocol': the Orgus Project Protocol, version 1
 * (docs/architecture/project-protocol-v1.md, ADR 0052).
 *
 * A project describes itself in a committed `orgus.yml` and reports at run time through up to
 * three JSON files in `<git-common-dir>/orgus/`. The agent reads them (read-only), the hub keeps
 * what agents report, adds the checks it runs on every project (sessions, workflows, worktrees,
 * CPU), arbitrates heavy work around landings and deploys, and runs declared actions only after a
 * person confirms. Orgus never writes into a watched project.
 *
 * Two layers of types: the files as written (`*File`, ISO 8601 strings, exactly the spec) and the
 * shapes agents, hub and console exchange (`Ts` numbers, already checked).
 */
import type { ActionKind, Id, Pipeline, PipelineCapacity, PipelineCapacityClaim, PipelineItem, PipelineItemScope, Ts } from '../index.ts';
import type { CpuTopReport } from './project-protocol-hub.ts';

export const PROJECT_PROTOCOL_EXT = 'project-protocol';
/** The protocol major version this Orgus reads. */
export const PROJECT_PROTOCOL_VERSION = 1;
export const PROJECT_MANIFEST_FILE = 'orgus.yml';
/** Under `git rev-parse --git-common-dir`. */
export const PROJECT_STATE_DIR = 'orgus';
export const PROJECT_STATE_FILES = ['pipeline', 'health', 'deploys'] as const;
export type ProjectStateFileKind = (typeof PROJECT_STATE_FILES)[number];

/** Seconds between writes when neither the file nor the manifest says. */
export const PROJECT_DEFAULT_INTERVAL_S: Record<ProjectStateFileKind, number> = { pipeline: 15, health: 60, deploys: 300 };
/** A file older than this many intervals is not reporting. */
export const PROJECT_STALE_FACTOR = 3;
/** A timestamp this far in the future counts as now. */
export const PROJECT_CLOCK_SKEW_MS = 5 * 60_000;
export const PROJECT_FILE_MAX_BYTES = 1024 * 1024;
export const PROJECT_ITEMS_MAX = 500;
export const PROJECT_TEXT_MAX = 500;
/** deploys.json: the longest `last.message` (why the last deploy ended so). */
export const DEPLOY_MESSAGE_MAX = 300;
export const PROJECT_ACTION_TIMEOUT_DEFAULT_S = 600;
export const PROJECT_ACTION_TIMEOUT_MAX_S = 7200;
export const PROJECT_ACTION_ID_RE = /^[a-z0-9][a-z0-9-]{0,39}$/;
export const PROJECT_PROBLEM_ID_RE = /^[A-Za-z0-9._:-]{1,100}$/;
/** Nothing waits longer than this for one landing or deploy. */
export const PROJECT_YIELD_MAX_MS = 30 * 60_000;
/** A critical problem that clears and comes back notifies again only after this long. */
export const PROJECT_RENOTIFY_MS = 30 * 60_000;

// ---------------------------------------------------------------------------
// The manifest, as committed (orgus.yml)
// ---------------------------------------------------------------------------

export type ProjectActionRole = 'pipeline.pause' | 'pipeline.resume' | 'pipeline.restart';
export const PROJECT_ACTION_ROLES: readonly ProjectActionRole[] = ['pipeline.pause', 'pipeline.resume', 'pipeline.restart'];

export interface ProjectActionDecl {
  id: string;
  label: string;
  /** The exact command, run as written through the platform shell. */
  run: string;
  /** What it does, for the person who confirms. */
  does: string;
  /** Relative to the main checkout; default '.'. */
  cwd: string;
  confirm: boolean;
  role?: ProjectActionRole;
  /** Optional heavy work: waits while a landing or deploy is in progress (spec section 6). */
  heavy: boolean;
  timeoutS: number;
  /** `every: <minutes>` (ORGU-294): the hub runs it on that schedule, no person. Non-heavy only. */
  everyMin?: number;
}

/** The longest `every:` Orgus honours: one day. */
export const PROJECT_ACTION_EVERY_MAX_MIN = 1440;

export interface ProjectManifest {
  protocol: number;
  name: string;
  description?: string;
  pipeline?: { title?: string; intervalS?: number };
  health?: { intervalS?: number };
  deploys?: { intervalS?: number };
  /** Folders Orgus never offers to remove, absolute (resolved by the agent). */
  protect: string[];
  actions: ProjectActionDecl[];
  /** Worktree setup executable and arguments, run without a shell before launch. */
  setup?: string[];
  /** How work reaches main (ADR 0060): Orgus reminds agents to land small and often. */
  flow?: ProjectFlow;
  /** What Orgus may deploy for the project on its own (ORGU-290); absent is delivery off. */
  delivery?: DeliveryPolicy;
}

/** When a delivery runs: after a landing, once main is green. */
export const DELIVERY_TRIGGERS = ['green-main-after-landing'] as const;
export type DeliveryTrigger = (typeof DELIVERY_TRIGGERS)[number];

/** A weekly window, UTC, in which no delivery starts. */
export interface DeliveryFreezeWindow {
  /** 0 Sunday .. 6 Saturday; empty is every day. */
  days: number[];
  /** "HH:MM" UTC, start inclusive, end exclusive; end before start crosses midnight. */
  from: string;
  to: string;
}

/**
 * `delivery:` in orgus.yml (ORGU-290). The target must be a declared action. Off (or absent),
 * nothing changes: a heavy action still waits for a person to confirm it.
 */
export interface DeliveryPolicy {
  enabled: boolean;
  target: string;
  trigger: DeliveryTrigger;
  freeze: DeliveryFreezeWindow[];
  maxPerDay: number;
}

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * `flow:` in orgus.yml (ADR 0060). Orgus watches every controlled session working in the
 * project: when its branch holds work off main for too long, too many commits or too many
 * uncommitted lines, the agent is told to commit and land, and is not let to end its turn
 * with that work unlanded. Orgus never lands anything itself: it names `land` to the agent.
 */
export interface ProjectFlow {
  /** The branch work lands on. */
  main: string;
  /** The exact command an agent runs to land its branch, shown to it (never run by Orgus). */
  land?: string;
  /** Minutes the oldest commit off main may wait before the agent is told to land. */
  remindAfterMin: number;
  /** Commits off main before the agent is told to land. */
  maxUnlandedCommits: number;
  /** Uncommitted lines (added plus removed) before the agent is told to commit. */
  maxUncommittedLines: number;
  /** true: past twice a limit, file edits are refused until the branch lands. */
  enforce: boolean;
}

export const PROJECT_FLOW_DEFAULTS: Omit<ProjectFlow, 'land'> = { main: 'main', remindAfterMin: 45, maxUnlandedCommits: 8, maxUncommittedLines: 400, enforce: false };

// ---------------------------------------------------------------------------
// The state files, as written
// ---------------------------------------------------------------------------

export type ProjectStageKind = 'queue' | 'verify' | 'landing' | 'deploy' | 'done' | 'other';
export const PROJECT_STAGE_KINDS: readonly ProjectStageKind[] = ['queue', 'verify', 'landing', 'deploy', 'done', 'other'];

interface StateFileBase {
  protocol: number;
  /** ISO 8601 with a zone. */
  updatedAt: string;
  /** Seconds until the next write; overrides the manifest. */
  interval?: number;
}

export interface PipelineFileClaim {
  label: string;
  slots: number;
  run?: string;
  pid?: number;
  since?: string;
  reserved?: boolean;
}

export interface PipelineFile extends StateFileBase {
  title?: string;
  state: 'running' | 'idle' | 'paused' | 'down' | 'error';
  error?: string | null;
  /** Why nothing moves although work waits (main is red, so landings wait): shown above the queue. */
  hold?: string | null;
  stages: { id: string; title: string; kind?: ProjectStageKind }[];
  items: {
    id: string;
    title: string;
    detail?: string | null;
    stage: string;
    state: 'queued' | 'active' | 'passed' | 'failed' | 'cancelled';
    author?: string | null;
    startedAt?: string | null;
    stageStartedAt?: string | null;
    endedAt?: string | null;
    /** Relative to the git common dir, or absolute. */
    log?: string | null;
    priority?: boolean;
    /** A finer state within the stage, for example 'probing' or 'pushing' (at most 40 characters). */
    phase?: string | null;
    /** At most 10 short labels, for example the deploy targets a submission asks for. */
    tags?: string[];
    /** What the change touches: files changed and the units (at most 30), for forecasts (ADR 0091). */
    scope?: PipelineItemScope;
  }[];
  batch?: { size: number; unit?: string } | null;
  capacity?: {
    label: string;
    used: number;
    total: number;
    waiting: number;
    holders?: PipelineFileClaim[];
    queue?: PipelineFileClaim[];
    runs?: number;
    runLabel?: string;
    reserve?: { slots: number; inUse: number; for: string };
  } | null;
}

export type ProblemSeverity = 'critical' | 'warning' | 'info';
export const PROBLEM_SEVERITIES: readonly ProblemSeverity[] = ['critical', 'warning', 'info'];
export const PROBLEM_SEVERITY_LABEL: Record<ProblemSeverity, string> = { critical: 'Critical', warning: 'Warning', info: 'Info' };

export interface HealthFile extends StateFileBase {
  ok: boolean;
  problems: { id: string; severity: ProblemSeverity; what: string; why: string; next: string; since?: string | null; action?: string | null }[];
}

export type DeployResult = 'succeeded' | 'failed' | 'running' | 'cancelled' | 'rolled-back';
/** Who put the live version there, as far as the project knows. */
export type DeploySource = 'pipeline' | 'manual' | 'unknown';

export interface DeploysFile extends StateFileBase {
  targets: {
    id: string;
    label: string;
    url?: string | null;
    state?: 'idle' | 'deploying';
    /** A finer state, for example serving, building, draining or failed (at most 40 characters). */
    phase?: string | null;
    live?: { version: string; at?: string | null; source?: DeploySource } | null;
    main?: { version: string } | null;
    /** `message`: why it ended so, in words ("Refused: main is red (...)."), at most 300 characters. */
    last?: { result: DeployResult; version?: string | null; at?: string | null; log?: string | null; message?: string | null } | null;
  }[];
}

// ---------------------------------------------------------------------------
// What agents report (agent -> hub), checked and in Ts
// ---------------------------------------------------------------------------

/** How one state file stands. 'absent': not written and not declared, so nothing is expected. */
/** 'idle': the file is old because its runner stopped on purpose (spec section 4), not a fault. */
export type ReportingState = 'reporting' | 'not-reporting' | 'idle' | 'absent';
export const REPORTING_STATE_LABEL: Record<ReportingState, string> = {
  reporting: 'Reporting',
  'not-reporting': 'Not reporting',
  idle: 'Idle',
  absent: 'Not used',
};

export interface StateFileStatus {
  kind: ProjectStateFileKind;
  state: ReportingState;
  /** The file's own updatedAt, when it could be read. */
  updatedAt: Ts | null;
  intervalS: number;
  /** Why it is not reporting, in words: "Older than 3 x 10 s", "Line 1: not JSON", "Protocol 2 is newer than this Orgus reads (1)". */
  reason?: string;
}

export interface Problem {
  /** Unique in the hub: `<source>:<projectId or machineId>:<id>`. */
  key: string;
  /** The project's id (health.json) or the check's id (Orgus). */
  id: string;
  source: 'project' | 'orgus';
  /** For source 'orgus': which check found it. */
  check?: NativeCheckId;
  severity: ProblemSeverity;
  what: string;
  why: string;
  next: string;
  projectId: Id | null;
  machineId: Id | null;
  since: Ts;
  /** A declared action that helps (health.json's `action`). */
  actionId?: string;
  /** What the problem is about, for links: a session, workflow run, worktree path or process. */
  subject?: { kind: 'session' | 'workflow' | 'worktree' | 'machine' | 'pipeline-item' | 'deploy-target'; id: string; label?: string };
}

export interface DeployTarget {
  id: string;
  label: string;
  url: string | null;
  deploying: boolean;
  /** The target's own finer state ("serving", "building"), when the file says it. */
  phase?: string;
  live: { version: string; at: Ts | null; source: DeploySource } | null;
  mainVersion: string | null;
  /** live differs from main. */
  behind: boolean;
  /** `message`: why the last deploy ended so (a refusal, a failure), when the project says it. */
  last: { result: DeployResult; version: string | null; at: Ts | null; logRef: string | null; message?: string } | null;
}

/** A landing or deploy in progress: what makes heavy work on its machine wait. */
export interface ProjectBusy {
  kind: 'landing' | 'deploy';
  projectId: Id;
  machineId: Id;
  /** "landing 5 submissions", "deploying production". */
  what: string;
  since: Ts;
}

/** One project as the agent reads it (sent on change and at least every 30 s). */
export interface ProjectProtocolReport {
  projectId: Id;
  /** Committed manifest at HEAD, or null when the project has none. */
  manifest: ProjectManifest | null;
  /** Why orgus.yml at HEAD could not be used, when it exists but is invalid. */
  manifestError?: string;
  files: StateFileStatus[];
  /** health.json's problems (source 'project'); empty when not reporting. */
  problems: Problem[];
  deploys: DeployTarget[];
  busy: ProjectBusy[];
  /** Whether the pipeline came from this protocol or a fallback adapter (temporary, R-PP-6). */
  pipelineSource: 'protocol' | 'fallback' | 'none';
  readAt: Ts;
}

// ---------------------------------------------------------------------------
// Checks Orgus runs on every project
// ---------------------------------------------------------------------------

export type NativeCheckId = 'session-waiting' | 'workflow-stalled' | 'worktree-orphaned' | 'cpu-saturated' | 'project-not-reporting' | 'fix-failed';
export const NATIVE_CHECK_LABEL: Record<NativeCheckId, string> = {
  'session-waiting': 'Session waits for you',
  'workflow-stalled': 'Workflow makes no progress',
  'worktree-orphaned': 'Worktree held by an ended session',
  'cpu-saturated': 'Machine CPU saturated',
  'project-not-reporting': 'Project not reporting',
  'fix-failed': 'Orgus could not fix it',
};

export interface NativeCheckSettings {
  sessionWaitingWarnMin: number;
  sessionWaitingCriticalMin: number;
  workflowStalledWarnMin: number;
  workflowStalledCriticalMin: number;
  worktreeOrphanedWarnMin: number;
  cpuWarnPct: number;
  cpuWarnMin: number;
  cpuCriticalPct: number;
  cpuCriticalMin: number;
  /**
   * Organisation policy: every declared action asks for confirmation, even one the manifest
   * marks `confirm: false`. Absent means false. (Added by the hub module, 17 Oct 2026.)
   */
  alwaysConfirmActions?: boolean;
  /**
   * ADR 0064: Orgus fixes project problems on its own. A problem open past autoFixAfterMin gets a
   * fix session (the dispatch a person would start), at most autoFixMaxAttempts times; then a
   * person is asked once. On by default (the owner, 18 Oct 2026); absent reads as the default.
   */
  autoFix?: boolean;
  autoFixAfterMin: number;
  autoFixMaxAttempts: number;
  /** Who turned autoFix on: fixes act on that person's behalf (personal scope, ADR 0042); unset, the hub's owner. Set by the hub. */
  autoFixBy?: string;
  /**
   * Module 'dev-servers': stop a dev server or preview by itself once the session that started it
   * has ended. Never one whose session is live, never one Orgus cannot attribute. Off by default;
   * absent reads as off. (Added 22 Oct 2026.)
   */
  stopIdleDevServers?: boolean;
}

export const DEFAULT_NATIVE_CHECK_SETTINGS: NativeCheckSettings = {
  sessionWaitingWarnMin: 15,
  sessionWaitingCriticalMin: 60,
  workflowStalledWarnMin: 20,
  workflowStalledCriticalMin: 60,
  worktreeOrphanedWarnMin: 30,
  cpuWarnPct: 90,
  cpuWarnMin: 5,
  cpuCriticalPct: 97,
  cpuCriticalMin: 10,
  autoFix: true,
  autoFixAfterMin: 10,
  autoFixMaxAttempts: 3,
};

/** Who uses the CPU, for the cpu-saturated problem and the machine view. */
export interface CpuConsumer {
  /** 'tsc', 'playwright', 'go test', 'claude', 'Defender', or the process name. */
  label: string;
  pct: number;
  processes: number;
  projectId?: Id | null;
  sessionId?: Id | null;
}

// ---------------------------------------------------------------------------
// Arbitration
// ---------------------------------------------------------------------------

export type YieldWorkKind = 'mission' | 'session' | 'ai-job' | 'orgus-check' | 'declared-action';
export const YIELD_WORK_LABEL: Record<YieldWorkKind, string> = {
  mission: 'Mission',
  session: 'Headless session',
  'ai-job': 'AI job',
  'orgus-check': 'Orgus check',
  'declared-action': 'Project action',
};

/** One piece of work that waits for a landing or deploy. */
export interface YieldHold {
  id: Id;
  kind: YieldWorkKind;
  /** The waiting work's own id: the queue entry, AI job or action run. (Added 17 Oct 2026.) */
  workId?: Id;
  /** What waits, in words. */
  label: string;
  /** The project the waiting work belongs to (null for Orgus's own work). */
  projectId: Id | null;
  machineId: Id;
  /** What it waits for. */
  for: ProjectBusy;
  since: Ts;
  /** It starts anyway at this time (since + PROJECT_YIELD_MAX_MS). */
  until: Ts;
  state: 'waiting' | 'released' | 'timed-out' | 'started-now';
  releasedAt?: Ts;
  /** Who chose "Start now", for state 'started-now'. */
  by?: string;
}

// ---------------------------------------------------------------------------
// Remediation
// ---------------------------------------------------------------------------

export type ActionRunState = 'awaiting-confirmation' | 'running' | 'succeeded' | 'failed' | 'cancelled' | 'refused';

export interface ProjectActionRun {
  id: Id;
  projectId: Id;
  machineId: Id;
  actionId: string;
  /** The command as declared at HEAD when it was confirmed. */
  command: string;
  cwd: string;
  state: ActionRunState;
  requestedBy: string;
  confirmedBy?: string;
  /**
   * When Orgus confirmed it under a delivery policy (ORGU-291): confirmedBy is 'orgus', this is
   * the owner it acted for, and `confirmation` reads "Orgus on behalf of <owner>".
   */
  confirmedOnBehalfOf?: string;
  confirmation?: string;
  /** The delivery policy that confirmed it: 'delivery:<projectId>' (orgus.yml `delivery:`). */
  deliveryPolicyId?: Id;
  startedAt?: Ts;
  endedAt?: Ts;
  exitCode?: number | null;
  /** Why it was refused or failed, in words. */
  reason?: string;
  logRef?: string;
  /** The end of the output (at most 4 KB), from the agent's action.done. (Added 17 Oct 2026.) */
  tail?: string;
  /** Waiting for a landing (heavy actions). */
  yieldId?: Id;
}

/** "Dispatch a fix": a Claude Code session started in the project with the diagnosis attached. */
export interface FixDispatch {
  id: Id;
  problemKey: string;
  projectId: Id;
  machineId: Id;
  sessionId: Id | null;
  state: 'starting' | 'started' | 'failed';
  by: string;
  at: Ts;
  reason?: string;
  /** ADR 0064: started by Orgus on its own, and which attempt for this problem it is (1, 2, ...). */
  auto?: boolean;
  attempt?: number;
  /** The problem cleared while or after this fix ran: Orgus fixed it (proven by the check). */
  resolvedAt?: Ts;
  /** The last allowed attempt ended and the problem stayed: a person is asked (check fix-failed). */
  gaveUp?: boolean;
  /**
   * ADR 0064 amended 20 Oct 2026: the coding agent the scheduler chose for the fix, and the
   * permission mode it runs in. Absent on fixes from older hubs and without module 'scheduler'
   * (Claude Code, 'default'). Additive.
   */
  agentKind?: string;
  permissionMode?: string;
  /**
   * The fix never started because no coding agent could run it: each one tried failed for its own
   * reason (a usage limit, signed out) or none was free. It is not an attempt: Orgus fixes it
   * retries it later (planAutoFix) and never gives up on it. Additive, 20 Oct 2026.
   */
  noAgent?: boolean;
}

// ---------------------------------------------------------------------------
// Hub API
// ---------------------------------------------------------------------------

/** GET /api/project-protocol */
export interface ProjectProtocolState {
  projects: (ProjectProtocolReport & { machineId: Id })[];
  /** Every open problem, project and Orgus, most severe first. */
  problems: Problem[];
  yields: YieldHold[];
  runs: ProjectActionRun[];
  dispatches: FixDispatch[];
  settings: NativeCheckSettings;
  /** Who uses the CPU per machine, from agents that run module 'cpu-top' (added 17 Oct 2026). */
  cpu?: (CpuTopReport & { machineId: Id })[];
  /** ORGU-292: per project, the delivery policy (added 30 Oct 2026). */
  delivery?: ProjectDelivery[];
}

/**
 * ORGU-292, deliver past main: with delivery on, each new main commit (deploys.json `main.version`)
 * on a green main (no critical project problem) runs the project's declared target action once,
 * within one hub pass, with no confirmation: turning delivery on is the standing confirmation.
 */
export interface ProjectDelivery {
  projectId: Id;
  /** The declared action that deploys (orgus.yml `actions`). */
  actionId: string;
  /** Who turned it on: runs act for them. */
  by: string;
  /** The last main commit seen: a commit is delivered only when it differs from this one. */
  seen: string | null;
  /** The last main commit a run was requested for (one per commit), and that run. */
  requested?: { version: string; runId: Id; at: Ts } | null;
}

/**
 * Routes (all under /api/project-protocol):
 *   GET    /                                   ProjectProtocolState
 *   POST   /actions/run       { projectId, machineId, actionId }   -> ProjectActionRun (awaiting-confirmation, or running when confirm: false)
 *   POST   /actions/:runId/confirm                                 -> ProjectActionRun
 *   POST   /actions/:runId/cancel                                  -> ProjectActionRun
 *   POST   /dispatch          { problemKey, note?, mode? }         -> FixDispatch   (mode 'headless' default, or 'terminal')
 *   POST   /yields/:id/start-now                                   -> YieldHold
 *   PUT    /settings          NativeCheckSettings                  -> NativeCheckSettings
 *   PUT    /delivery/:projectId { actionId | null }                -> ProjectDelivery | null  (admin; null turns delivery off)
 *
 * Agent frames (ext 'project-protocol'):
 *   agent -> hub  'report'      { reports: ProjectProtocolReport[] }
 *   hub -> agent  'action.run'  { runId, projectId, actionId, command }  (the agent re-reads orgus.yml at HEAD and runs the action only if it is still declared with exactly this command)
 *   agent -> hub  'action.done' { runId, exitCode, logRef, reason?, tail? }   (exitCode null with no log and no tail: it never ran, refused; the hub also accepts refused: true)
 *   hub -> agent  'request'     {}  (resend the reports now; the hub sends it when an agent connects)
 *   agent -> hub  'cpu-top'     CpuTopReport  (agent module 'cpu-top')
 */
export const PROJECT_PROTOCOL_API = '/api/project-protocol';

// ---------------------------------------------------------------------------
// Checking and mapping (pure, browser-safe, no I/O). The agents read the files; these decide
// what they mean. `orgus project check` and the hub use the same functions.
// ---------------------------------------------------------------------------

/** The pipeline adapter that reads pipeline.json. */
export const PROJECT_PROTOCOL_ADAPTER = 'orgus-protocol';

/**
 * The stage id of the pipeline `orgus land` publishes (tools/orgus-cli/src/project-state.ts): a
 * pipeline with it is a project landing continuously with orgus land (ADR 0055).
 */
export const ORGUS_LAND_STAGE = 'orgus-land';
export const landsWithOrgusLand = (p: { stages: readonly { id: string }[] } | null | undefined): boolean => !!p?.stages.some((s) => s.id === ORGUS_LAND_STAGE);
/**
 * The stage of an agent's own checks before it submits (ORGU-83): a work-budget lease in one of
 * the repo's worktrees that is not a landing, published by the same pipeline as the landings.
 */
export const AGENT_CHECKS_STAGE = 'agent-checks';
/** The stage of a landing that has started and waits for CPU in the work budget (ADR 0063). */
export const ORGUS_WAITING_STAGE = 'waiting';
/**
 * The tags `orgus land` puts on a failed landing in pipeline.json (ORGU-83): what became of it.
 * `landed-since`: the branch landed later, so the failure is settled (the flow does not count it).
 * `returned`: its agent has it (woken with what failed, or the branch is back in the queue).
 * `stuck`: nobody has it (no session to wake, or the wake failed), so a person must act.
 * `gone`: the branch was deleted since, so nobody can act on it (the flow does not count it).
 * A branch whose work reached main under another branch counts as `landed-since`.
 */
export const LANDING_FATE = { landedSince: 'landed-since', returned: 'returned', stuck: 'stuck', gone: 'gone' } as const;
/** An agent checks a state file at most this often, and re-reads it only when its size or mtime changed. */
export const PROJECT_STAT_GATE_MS = 2_000;
/** An agent sends its reports on change and at least this often. */
export const PROJECT_REPORT_EVERY_MS = 30_000;
/** Recent (finished) pipeline items shown; the same as PIPELINE_RECENT_MAX. */
const RECENT_MAX = 20;
/** pipeline.json items: `phase` length, and how many `tags` of what length. */
export const PROJECT_PHASE_MAX = 40;
export const PROJECT_TAGS_MAX = 10;
export const PROJECT_TAG_MAX = 40;
/** pipeline.json items: how many units a `scope` lists, and how long each may be (ADR 0091). */
export const PROJECT_SCOPE_UNITS_MAX = 30;
export const PROJECT_SCOPE_UNIT_MAX = 80;
/** Intervals (seconds) a manifest or state file may give. */
export const PROJECT_INTERVAL_MIN_S = 1;
export const PROJECT_INTERVAL_MAX_S = 86_400;

export type ManifestResult = { manifest: ProjectManifest; warnings: string[] } | { error: string; errors: string[] };
/**
 * A state file read: the checked file, or why it cannot be used. `violations` lists what broke the
 * spec but was tolerated (a text cut to the limit, a non-web URL dropped, a wrong `ok`): Orgus
 * still shows the file, `orgus project check` fails on it.
 */
export type StateFileResult<F> = { file: F; violations: string[] } | { error: string };

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:\d{2})$/i;
const ISO_HINT = 'ISO 8601 with a zone, for example 2026-10-17T20:41:00Z';

/** An ISO 8601 time with a zone, as epoch ms; null when it is not one. */
export function parseIsoTs(v: unknown): Ts | null {
  if (typeof v !== 'string' || !ISO_RE.test(v)) return null;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : null;
}

/** UTF-8 length of a string, without TextEncoder (not in every runtime's types). */
export function utf8Length(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      n += 4;
      i++;
    } else n += 3;
  }
  return n;
}

function joinErrors(errors: string[]): string {
  const shown = errors.slice(0, 3).join('; ');
  return errors.length > 3 ? `${shown}; and ${errors.length - 3} more` : shown;
}

/** Collects errors (the file cannot be used) and notes (warnings, or violations Orgus tolerates), each saying where. */
class Checker {
  readonly errors: string[] = [];
  readonly notes: string[] = [];
  /** Text over the limit: an error (the manifest) or cut and noted (state files). */
  private readonly cutText: boolean;
  constructor(cutText: boolean) {
    this.cutText = cutText;
  }
  fail(where: string, msg: string): void {
    this.errors.push(where ? `${where}: ${msg}` : msg);
  }
  note(where: string, msg: string): void {
    this.notes.push(where ? `${where}: ${msg}` : msg);
  }
  private text(v: string, where: string, max: number, exact = false): string {
    if (v.length <= max) return v;
    if (exact || !this.cutText) {
      this.fail(where, `is longer than ${max} characters`);
      return v;
    }
    this.note(where, `is longer than ${max} characters, cut`);
    return `${v.slice(0, max - 1)}…`;
  }
  /** A required, non-blank string. `exact`: an id or version, never cut (too long is an error). */
  req(o: Obj, key: string, where: string, max = PROJECT_TEXT_MAX, exact = false): string {
    const v = o[key];
    const at = where ? `${where}.${key}` : key;
    if (v === undefined || v === null) {
      this.fail(at, 'is missing');
      return '';
    }
    if (typeof v !== 'string') {
      this.fail(at, 'must be text');
      return '';
    }
    if (!v.trim()) {
      this.fail(at, 'is empty');
      return '';
    }
    return this.text(v, at, max, exact);
  }
  /** An optional string (null and missing are undefined). */
  opt(o: Obj, key: string, where: string, max = PROJECT_TEXT_MAX): string | undefined {
    const v = o[key];
    const at = where ? `${where}.${key}` : key;
    if (v === undefined || v === null) return undefined;
    if (typeof v !== 'string') {
      this.fail(at, 'must be text');
      return undefined;
    }
    return this.text(v, at, max);
  }
  bool(o: Obj, key: string, where: string): boolean | undefined {
    const v = o[key];
    if (v === undefined || v === null) return undefined;
    if (typeof v !== 'boolean') {
      this.fail(where ? `${where}.${key}` : key, 'must be true or false');
      return undefined;
    }
    return v;
  }
  /** An optional number >= min (and whole when asked). */
  num(o: Obj, key: string, where: string, opts: { min?: number; int?: boolean; required?: boolean } = {}): number | undefined {
    const v = o[key];
    const at = where ? `${where}.${key}` : key;
    if (v === undefined || v === null) {
      if (opts.required) this.fail(at, 'is missing');
      return undefined;
    }
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      this.fail(at, 'must be a number');
      return undefined;
    }
    if (opts.int && !Number.isInteger(v)) {
      this.fail(at, 'must be a whole number');
      return undefined;
    }
    if (opts.min !== undefined && v < opts.min) {
      this.fail(at, `must be at least ${opts.min}`);
      return undefined;
    }
    return v;
  }
  oneOf<T extends string>(o: Obj, key: string, where: string, allowed: readonly T[], required: boolean): T | undefined {
    const v = o[key];
    const at = where ? `${where}.${key}` : key;
    if (v === undefined || v === null) {
      if (required) this.fail(at, `is missing (one of ${allowed.join(', ')})`);
      return undefined;
    }
    if (typeof v !== 'string' || !(allowed as readonly string[]).includes(v)) {
      this.fail(at, `must be one of ${allowed.join(', ')}`);
      return undefined;
    }
    return v as T;
  }
  /** An optional ISO time (null and missing are undefined), kept as written. */
  iso(o: Obj, key: string, where: string, required = false): string | undefined {
    const v = o[key];
    const at = where ? `${where}.${key}` : key;
    if (v === undefined || v === null) {
      if (required) this.fail(at, `is missing (${ISO_HINT})`);
      return undefined;
    }
    if (parseIsoTs(v) === null) {
      this.fail(at, `must be ${ISO_HINT}`);
      return undefined;
    }
    return v as string;
  }
  /** An optional list (null and missing are []), at most `max` entries. */
  list(o: Obj, key: string, where: string, max: number, required = false): unknown[] {
    const v = o[key];
    const at = where ? `${where}.${key}` : key;
    if (v === undefined || v === null) {
      if (required) this.fail(at, 'is missing (a list)');
      return [];
    }
    if (!Array.isArray(v)) {
      this.fail(at, 'must be a list');
      return [];
    }
    if (v.length > max) {
      this.fail(at, `has ${v.length} entries, at most ${max}`);
      return [];
    }
    return v;
  }
}

// ── The manifest ──────────────────────────────────────────────────────────────

/** The protocol version a document claims: an error in words when this Orgus cannot read it. */
function checkProtocol(v: unknown): string | null {
  if (v === undefined || v === null) return `protocol is missing (it must be ${PROJECT_PROTOCOL_VERSION})`;
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 1) return `protocol must be a whole number, ${PROJECT_PROTOCOL_VERSION} for this version`;
  if (v > PROJECT_PROTOCOL_VERSION) return `Protocol ${v} is newer than this Orgus reads (${PROJECT_PROTOCOL_VERSION})`;
  return null;
}

/** A section's interval in seconds, clamped (with a warning) to 1 s .. 1 day. */
function sectionInterval(c: Checker, o: Obj, where: string): number | undefined {
  const v = c.num(o, 'interval', where);
  if (v === undefined) return undefined;
  if (v < PROJECT_INTERVAL_MIN_S || v > PROJECT_INTERVAL_MAX_S) {
    const clamped = Math.min(PROJECT_INTERVAL_MAX_S, Math.max(PROJECT_INTERVAL_MIN_S, v));
    c.note(`${where}.interval`, `${v} s is out of range, ${clamped} s is used`);
    return clamped;
  }
  return v;
}

/** Why a cwd is not relative to the main checkout and inside it, or null when it is. */
function checkCwd(v: string): string | null {
  if (!v.trim()) return 'is empty';
  if (/^[\\/]/.test(v) || /^[A-Za-z]:/.test(v) || v.includes('\0')) return 'must be relative to the main checkout';
  if (v.split(/[\\/]+/).some((seg) => seg === '..')) return 'must stay inside the main checkout (no ..)';
  return null;
}

/**
 * Check an `orgus.yml` (already parsed from YAML 1.2, or JSON) against spec section 2. Unknown
 * keys are ignored. `protect` entries are kept as written: the agent resolves relative ones
 * against the main checkout. Warnings: values Orgus changed (a clamped timeout or interval,
 * `confirm: false` on a heavy action) and labels that do not start in sentence case.
 */
export function parseManifest(obj: unknown): ManifestResult {
  if (!isObj(obj)) {
    const error = obj === null || obj === undefined ? 'orgus.yml is empty' : 'orgus.yml must be a mapping (key: value lines) at the top level';
    return { error, errors: [error] };
  }
  const c = new Checker(false);
  const proto = checkProtocol(obj.protocol);
  if (proto) {
    // A newer protocol: nothing else is checked, the reason is all a person needs.
    if (proto.startsWith('Protocol ')) return { error: proto, errors: [proto] };
    c.fail('', proto);
  }
  const name = c.req(obj, 'name', '');
  const description = c.opt(obj, 'description', '');
  let setup: string[] | undefined;
  if ('setup' in obj) {
    if (!Array.isArray(obj.setup) || obj.setup.length < 1 || obj.setup.length > 100 || obj.setup.some((v) => typeof v !== 'string' || !v || v.length > 4000 || /[\r\n\0]/.test(v))) {
      c.fail('setup', 'must be a nonempty list of executable and arguments, each a single line');
    } else setup = [...obj.setup] as string[];
  }

  const section = (key: 'pipeline' | 'health' | 'deploys'): { title?: string; intervalS?: number } | undefined => {
    if (!(key in obj)) return undefined;
    const v = obj[key];
    // `pipeline:` with nothing under it declares the section with its defaults.
    if (v === null) return {};
    if (!isObj(v)) {
      c.fail(key, 'must be a mapping (or empty)');
      return undefined;
    }
    const out: { title?: string; intervalS?: number } = {};
    if (key === 'pipeline') {
      const title = c.opt(v, 'title', key);
      if (title !== undefined) out.title = title;
    }
    const intervalS = sectionInterval(c, v, key);
    if (intervalS !== undefined) out.intervalS = intervalS;
    return out;
  };
  const pipeline = section('pipeline');
  const health = section('health');
  const deploys = section('deploys');

  const protect: string[] = [];
  c.list(obj, 'protect', '', 100).forEach((p, i) => {
    if (typeof p !== 'string' || !p.trim()) c.fail(`protect[${i}]`, 'must be a folder path');
    else protect.push(p);
  });

  const actions: ProjectActionDecl[] = [];
  const ids = new Set<string>();
  const roles = new Map<ProjectActionRole, string>();
  c.list(obj, 'actions', '', 100).forEach((a, i) => {
    const at = `actions[${i}]`;
    if (!isObj(a)) {
      c.fail(at, 'must be a mapping with id, label, run and does');
      return;
    }
    const before = c.errors.length;
    const id = c.req(a, 'id', at, 40);
    const where = id ? `actions.${id}` : at;
    if (id && !PROJECT_ACTION_ID_RE.test(id)) c.fail(`${at}.id`, `"${id}" must be 1 to 40 of a-z, 0-9 and -, starting with a letter or digit`);
    else if (id && ids.has(id)) c.fail(`${at}.id`, `"${id}" is declared twice`);
    if (id) ids.add(id);
    const label = c.req(a, 'label', where);
    const run = c.req(a, 'run', where, 4000);
    if (run && /[\r\n\0]/.test(run)) c.fail(`${where}.run`, 'must be one line (the platform shells do not run several lines the same way)');
    const does = c.req(a, 'does', where);
    let cwd = c.opt(a, 'cwd', where) ?? '.';
    const cwdErr = checkCwd(cwd);
    if (cwdErr) {
      c.fail(`${where}.cwd`, cwdErr);
      cwd = '.';
    }
    const role = c.oneOf(a, 'role', where, PROJECT_ACTION_ROLES, false);
    const heavy = c.bool(a, 'heavy', where) ?? false;
    let confirm = c.bool(a, 'confirm', where) ?? true;
    if (!confirm && heavy) {
      c.note(`${where}.confirm`, 'false is not honoured on a heavy action: Orgus asks before it runs');
      confirm = true;
    }
    if (role && heavy) c.fail(`${where}.heavy`, `an action with role ${role} cannot be heavy: Pause, Resume and Restart never wait for a landing`);
    if (role) {
      const other = roles.get(role);
      if (other !== undefined) c.fail(`${where}.role`, `${role} is already the role of ${other}`);
      else roles.set(role, id || at);
    }
    let timeoutS = c.num(a, 'timeout', where) ?? PROJECT_ACTION_TIMEOUT_DEFAULT_S;
    if (timeoutS < 1 || timeoutS > PROJECT_ACTION_TIMEOUT_MAX_S) {
      const clamped = Math.min(PROJECT_ACTION_TIMEOUT_MAX_S, Math.max(1, timeoutS));
      c.note(`${where}.timeout`, `${timeoutS} s is out of range, ${clamped} s is used`);
      timeoutS = clamped;
    }
    if (label && /^[a-z]/.test(label)) c.note(`${where}.label`, 'should start with a capital letter (sentence case, verb and object)');
    const everyMin = c.num(a, 'every', where, { min: 1, int: true });
    if (everyMin !== undefined && heavy) c.fail(`${where}.every`, 'every is allowed only on non-heavy actions: a heavy action waits for a person to confirm it');
    if (c.errors.length > before) return;
    const decl: ProjectActionDecl = { id, label, run, does, cwd, confirm, heavy, timeoutS };
    if (role) decl.role = role;
    if (everyMin !== undefined) decl.everyMin = Math.min(everyMin, PROJECT_ACTION_EVERY_MAX_MIN);
    actions.push(decl);
  });

  let flow: ProjectFlow | undefined;
  if ('flow' in obj) {
    const v = obj.flow;
    if (v !== null && !isObj(v)) c.fail('flow', 'must be a mapping (or empty)');
    else {
      const f: Obj = v ?? {};
      const d = PROJECT_FLOW_DEFAULTS;
      const main = c.opt(f, 'main', 'flow', 200) ?? d.main;
      const land = c.opt(f, 'land', 'flow', 4000);
      if (land && /[\r\n\0]/.test(land)) c.fail('flow.land', 'must be one line');
      const count = (key: string, def: number, max: number): number => {
        const n = c.num(f, key, 'flow', { min: 1, int: true });
        if (n === undefined) return def;
        if (n > max) {
          c.note(`flow.${key}`, `${n} is out of range, ${max} is used`);
          return max;
        }
        return n;
      };
      flow = {
        main,
        remindAfterMin: count('remindAfter', d.remindAfterMin, 24 * 60),
        maxUnlandedCommits: count('maxUnlandedCommits', d.maxUnlandedCommits, 1000),
        maxUncommittedLines: count('maxUncommittedLines', d.maxUncommittedLines, 100_000),
        enforce: c.bool(f, 'enforce', 'flow') ?? d.enforce,
        ...(land ? { land } : {}),
      };
    }
  }

  let delivery: DeliveryPolicy | undefined;
  if (obj.delivery !== undefined && obj.delivery !== null) {
    const v = obj.delivery;
    if (!isObj(v)) c.fail('delivery', 'must be a mapping');
    else {
      const target = c.req(v, 'target', 'delivery', 40, true);
      if (target && !ids.has(target)) c.fail('delivery.target', `"${target}" is not a declared action (declare it under actions, or name one of: ${[...ids].join(', ') || 'none'})`);
      const trigger = c.oneOf(v, 'trigger', 'delivery', DELIVERY_TRIGGERS, false) ?? 'green-main-after-landing';
      const freeze: DeliveryFreezeWindow[] = [];
      c.list(v, 'freeze', 'delivery', 50).forEach((w, i) => {
        const at = `delivery.freeze[${i}]`;
        if (!isObj(w)) return c.fail(at, 'must be a mapping with from and to (HH:MM UTC)');
        const from = c.req(w, 'from', at, 5, true);
        const to = c.req(w, 'to', at, 5, true);
        if (from && !HHMM_RE.test(from)) c.fail(`${at}.from`, 'must be HH:MM (UTC)');
        if (to && !HHMM_RE.test(to)) c.fail(`${at}.to`, 'must be HH:MM (UTC)');
        const days: number[] = [];
        c.list(w, 'days', at, 7).forEach((d, j) => {
          if (typeof d !== 'number' || !Number.isInteger(d) || d < 0 || d > 6) c.fail(`${at}.days[${j}]`, 'must be 0 (Sunday) to 6 (Saturday)');
          else days.push(d);
        });
        freeze.push({ days, from, to });
      });
      const maxPerDay = c.num(v, 'maxPerDay', 'delivery', { min: 1, int: true }) ?? 1;
      if (maxPerDay > 100) c.fail('delivery.maxPerDay', 'must be at most 100');
      delivery = { enabled: c.bool(v, 'enabled', 'delivery') ?? false, target, trigger, freeze, maxPerDay };
    }
  }

  if (c.errors.length) return { error: joinErrors(c.errors), errors: c.errors };
  const manifest: ProjectManifest = { protocol: obj.protocol as number, name, protect, actions };
  if (setup) manifest.setup = setup;
  if (flow) manifest.flow = flow;
  if (delivery) manifest.delivery = delivery;
  if (description !== undefined) manifest.description = description;
  if (pipeline) manifest.pipeline = pipeline;
  if (health) manifest.health = health;
  if (deploys) manifest.deploys = deploys;
  return { manifest, warnings: c.notes };
}

/** A declared action by id. */
export function declaredAction(manifest: ProjectManifest | null | undefined, id: string): ProjectActionDecl | null {
  return manifest?.actions.find((a) => a.id === id) ?? null;
}

/** The action bound to a pipeline button. */
export function actionForRole(manifest: ProjectManifest | null | undefined, role: ProjectActionRole): ProjectActionDecl | null {
  return manifest?.actions.find((a) => a.role === role) ?? null;
}

// ── State files ───────────────────────────────────────────────────────────────

/** A JSON.parse error as "Line 3: not JSON (Expected ...)". */
function jsonError(text: string, e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  const detail = msg.replace(/\s*in JSON at position \d+[\s\S]*$/, '').replace(/^Unexpected end of JSON input$/, 'it ends too early');
  let line: number | null = null;
  const lc = /\(line (\d+) column \d+\)/.exec(msg);
  if (lc) line = Number(lc[1]);
  else {
    const pos = /at position (\d+)/.exec(msg);
    if (pos) line = text.slice(0, Number(pos[1])).split('\n').length;
    else if (/end of JSON/.test(msg)) line = text.split('\n').length;
  }
  return `${line !== null ? `Line ${line}: n` : 'N'}ot JSON (${detail})`;
}

interface Base {
  obj: Obj;
  c: Checker;
  base: { protocol: number; updatedAt: string; interval?: number };
}

function parseBase(text: string, name: string): Base | { error: string } {
  if (typeof text !== 'string') return { error: `${name} could not be read` };
  if (text.length > PROJECT_FILE_MAX_BYTES || utf8Length(text) > PROJECT_FILE_MAX_BYTES) return { error: `${name} is larger than 1 MB` };
  const body = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  if (!body.trim()) return { error: `${name} is empty` };
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch (e) {
    return { error: jsonError(body, e) };
  }
  if (!isObj(parsed)) return { error: `${name} must hold one JSON object` };
  const proto = checkProtocol(parsed.protocol);
  if (proto) return { error: proto };
  const c = new Checker(true);
  const updatedAt = c.iso(parsed, 'updatedAt', '', true);
  const interval = c.num(parsed, 'interval', '');
  const base: Base['base'] = { protocol: parsed.protocol as number, updatedAt: updatedAt ?? '' };
  if (interval !== undefined) {
    if (interval < PROJECT_INTERVAL_MIN_S || interval > PROJECT_INTERVAL_MAX_S) c.fail('interval', `must be ${PROJECT_INTERVAL_MIN_S} to ${PROJECT_INTERVAL_MAX_S} seconds`);
    else base.interval = interval;
  }
  return { obj: parsed, c, base };
}

function done<F>(c: Checker, file: F): StateFileResult<F> {
  return c.errors.length ? { error: joinErrors(c.errors) } : { file, violations: c.notes };
}

const PIPELINE_STATES: readonly PipelineFile['state'][] = ['running', 'idle', 'paused', 'down', 'error'];
const ITEM_STATES: readonly PipelineFile['items'][number]['state'][] = ['queued', 'active', 'passed', 'failed', 'cancelled'];
const DEPLOY_RESULTS: readonly DeployResult[] = ['succeeded', 'failed', 'running', 'cancelled'];
const DEPLOY_SOURCES: readonly DeploySource[] = ['pipeline', 'manual', 'unknown'];

function claims(c: Checker, o: Obj, key: string, where: string): PipelineFileClaim[] | undefined {
  if (o[key] === undefined || o[key] === null) return undefined;
  const out: PipelineFileClaim[] = [];
  c.list(o, key, where, PROJECT_ITEMS_MAX).forEach((h, i) => {
    const at = `${where}.${key}[${i}]`;
    if (!isObj(h)) return c.fail(at, 'must be an object with label and slots');
    const claim: PipelineFileClaim = { label: c.req(h, 'label', at), slots: c.num(h, 'slots', at, { min: 0, required: true }) ?? 0 };
    const run = c.opt(h, 'run', at);
    if (run !== undefined) claim.run = run;
    const pid = c.num(h, 'pid', at, { min: 0, int: true });
    if (pid !== undefined) claim.pid = pid;
    const since = c.iso(h, 'since', at);
    if (since !== undefined) claim.since = since;
    const reserved = c.bool(h, 'reserved', at);
    if (reserved !== undefined) claim.reserved = reserved;
    out.push(claim);
  });
  return out;
}

/** An item's `scope` (ADR 0091): files changed and the units touched; a bad one is dropped with a note. */
function parseScope(c: Checker, it: Obj, at: string): PipelineItemScope | undefined {
  const s = it.scope;
  if (s === undefined || s === null) return undefined;
  if (!isObj(s)) {
    c.note(`${at}.scope`, 'must be an object with files and units; ignored');
    return undefined;
  }
  const files = s.files;
  if (typeof files !== 'number' || !Number.isInteger(files) || files < 0) {
    c.note(`${at}.scope.files`, 'must be a whole number of files; scope ignored');
    return undefined;
  }
  if (!Array.isArray(s.units) || s.units.some((u) => typeof u !== 'string' || !u.trim())) {
    c.note(`${at}.scope.units`, 'must be a list of unit names; scope ignored');
    return undefined;
  }
  if (s.units.length > PROJECT_SCOPE_UNITS_MAX) c.note(`${at}.scope.units`, `has ${s.units.length} units, the first ${PROJECT_SCOPE_UNITS_MAX} are kept`);
  const units = [...new Set((s.units as string[]).slice(0, PROJECT_SCOPE_UNITS_MAX).map((u) => u.slice(0, PROJECT_SCOPE_UNIT_MAX)))].sort();
  return { files, units, ...(s.docsOnly === true ? { docsOnly: true } : {}) };
}

/** Check pipeline.json (raw text) against spec section 3.1. */
export function parsePipelineFile(text: string): StateFileResult<PipelineFile> {
  const b = parseBase(text, 'pipeline.json');
  if ('error' in b) return b;
  const { obj, c } = b;
  const file: PipelineFile = { ...b.base, state: c.oneOf(obj, 'state', '', PIPELINE_STATES, true) ?? 'down', stages: [], items: [] };
  const title = c.opt(obj, 'title', '');
  if (title !== undefined) file.title = title;
  const error = c.opt(obj, 'error', '');
  if (error !== undefined) file.error = error;
  const hold = c.opt(obj, 'hold', '');
  if (hold) file.hold = hold;

  const stageIds = new Set<string>();
  c.list(obj, 'stages', '', 50, true).forEach((s, i) => {
    const at = `stages[${i}]`;
    if (!isObj(s)) return c.fail(at, 'must be an object with id and title');
    const id = c.req(s, 'id', at, 100, true);
    if (id && stageIds.has(id)) c.fail(`${at}.id`, `"${id}" is used twice`);
    stageIds.add(id);
    const stage: PipelineFile['stages'][number] = { id, title: c.req(s, 'title', at) };
    const kind = c.oneOf(s, 'kind', at, PROJECT_STAGE_KINDS, false);
    if (kind) stage.kind = kind;
    file.stages.push(stage);
  });

  const itemIds = new Set<string>();
  c.list(obj, 'items', '', PROJECT_ITEMS_MAX, true).forEach((it, i) => {
    const at = `items[${i}]`;
    if (!isObj(it)) return c.fail(at, 'must be an object');
    const id = c.req(it, 'id', at, 200, true);
    if (id && itemIds.has(id)) c.fail(`${at}.id`, `"${id}" is used twice`);
    itemIds.add(id);
    const stage = c.req(it, 'stage', at, 100, true);
    if (stage && !stageIds.has(stage)) c.fail(`${at}.stage`, `"${stage}" is not one of the stages`);
    const item: PipelineFile['items'][number] = { id, title: c.req(it, 'title', at), stage, state: c.oneOf(it, 'state', at, ITEM_STATES, true) ?? 'queued' };
    const detail = c.opt(it, 'detail', at);
    if (detail !== undefined) item.detail = detail;
    const author = c.opt(it, 'author', at);
    if (author !== undefined) item.author = author;
    for (const k of ['startedAt', 'stageStartedAt', 'endedAt'] as const) {
      const v = c.iso(it, k, at);
      if (v !== undefined) item[k] = v;
    }
    const log = c.opt(it, 'log', at, 1000);
    if (log !== undefined) item.log = log;
    const priority = c.bool(it, 'priority', at);
    if (priority !== undefined) item.priority = priority;
    const phase = c.opt(it, 'phase', at, PROJECT_PHASE_MAX);
    if (phase) item.phase = phase;
    const tags = c.list(it, 'tags', at, Number.MAX_SAFE_INTEGER);
    if (tags.length) {
      if (tags.length > PROJECT_TAGS_MAX) c.note(`${at}.tags`, `has ${tags.length} tags, the first ${PROJECT_TAGS_MAX} are kept`);
      const kept: string[] = [];
      tags.slice(0, PROJECT_TAGS_MAX).forEach((tag, j) => {
        if (typeof tag !== 'string' || !tag.trim()) return c.fail(`${at}.tags[${j}]`, 'must be text');
        if (tag.length <= PROJECT_TAG_MAX) return void kept.push(tag);
        c.note(`${at}.tags[${j}]`, `is longer than ${PROJECT_TAG_MAX} characters, cut`);
        kept.push(`${tag.slice(0, PROJECT_TAG_MAX - 1)}…`);
      });
      item.tags = kept;
    }
    const scope = parseScope(c, it, at);
    if (scope) item.scope = scope;
    file.items.push(item);
  });

  if (isObj(obj.batch)) {
    const size = c.num(obj.batch, 'size', 'batch', { min: 0, int: true, required: true });
    const unit = c.opt(obj.batch, 'unit', 'batch', 100);
    if (size !== undefined) file.batch = unit !== undefined ? { size, unit } : { size };
  } else if (obj.batch !== undefined && obj.batch !== null) c.fail('batch', 'must be an object with size');

  if (isObj(obj.capacity)) {
    const k = obj.capacity;
    const cap: NonNullable<PipelineFile['capacity']> = {
      label: c.req(k, 'label', 'capacity'),
      used: c.num(k, 'used', 'capacity', { min: 0, required: true }) ?? 0,
      total: c.num(k, 'total', 'capacity', { min: 0, required: true }) ?? 0,
      waiting: c.num(k, 'waiting', 'capacity', { min: 0 }) ?? 0,
    };
    const holders = claims(c, k, 'holders', 'capacity');
    if (holders) cap.holders = holders;
    const queue = claims(c, k, 'queue', 'capacity');
    if (queue) cap.queue = queue;
    const runs = c.num(k, 'runs', 'capacity', { min: 0, int: true });
    if (runs !== undefined) cap.runs = runs;
    const runLabel = c.opt(k, 'runLabel', 'capacity', 100);
    if (runLabel !== undefined) cap.runLabel = runLabel;
    if (isObj(k.reserve)) {
      const slots = c.num(k.reserve, 'slots', 'capacity.reserve', { min: 0, required: true });
      const inUse = c.num(k.reserve, 'inUse', 'capacity.reserve', { min: 0, required: true });
      const forWhat = c.req(k.reserve, 'for', 'capacity.reserve');
      if (slots !== undefined && inUse !== undefined) cap.reserve = { slots, inUse, for: forWhat };
    } else if (k.reserve !== undefined && k.reserve !== null) c.fail('capacity.reserve', 'must be an object with slots, inUse and for');
    file.capacity = cap;
  } else if (obj.capacity !== undefined && obj.capacity !== null) c.fail('capacity', 'must be an object');

  return done(c, file);
}

/** Check health.json (raw text) against spec section 3.2. A wrong `ok` is corrected and noted. */
export function parseHealthFile(text: string): StateFileResult<HealthFile> {
  const b = parseBase(text, 'health.json');
  if ('error' in b) return b;
  const { obj, c } = b;
  if (obj.ok === undefined || obj.ok === null) c.fail('ok', 'is missing (true or false)');
  const ok = c.bool(obj, 'ok', '');
  const file: HealthFile = { ...b.base, ok: ok ?? true, problems: [] };
  const ids = new Set<string>();
  c.list(obj, 'problems', '', PROJECT_ITEMS_MAX, true).forEach((p, i) => {
    const at = `problems[${i}]`;
    if (!isObj(p)) return c.fail(at, 'must be an object with id, severity, what, why and next');
    const id = c.req(p, 'id', at, 100, true);
    if (id && !PROJECT_PROBLEM_ID_RE.test(id)) c.fail(`${at}.id`, `"${id}" may hold only A-Z, a-z, 0-9 and . _ : -`);
    else if (id && ids.has(id)) c.fail(`${at}.id`, `"${id}" is used twice`);
    ids.add(id);
    const problem: HealthFile['problems'][number] = {
      id,
      severity: c.oneOf(p, 'severity', at, PROBLEM_SEVERITIES, true) ?? 'info',
      what: c.req(p, 'what', at),
      why: c.req(p, 'why', at),
      next: c.req(p, 'next', at),
    };
    const since = c.iso(p, 'since', at);
    if (since !== undefined) problem.since = since;
    const action = c.opt(p, 'action', at, 40);
    if (action !== undefined) {
      if (!PROJECT_ACTION_ID_RE.test(action)) c.fail(`${at}.action`, `"${action}" is not an action id`);
      else problem.action = action;
    }
    file.problems.push(problem);
  });
  const shouldBe = !file.problems.some((p) => p.severity === 'critical' || p.severity === 'warning');
  if (ok !== undefined && ok !== shouldBe) {
    c.note('ok', `is ${ok} but must be ${shouldBe}: it is true exactly when no problem is critical or a warning`);
    file.ok = shouldBe;
  }
  return done(c, file);
}

/** Check deploys.json (raw text) against spec section 3.3. A URL that is not http(s) is left out and noted. */
export function parseDeploysFile(text: string): StateFileResult<DeploysFile> {
  const b = parseBase(text, 'deploys.json');
  if ('error' in b) return b;
  const { obj, c } = b;
  const file: DeploysFile = { ...b.base, targets: [] };
  const ids = new Set<string>();
  c.list(obj, 'targets', '', PROJECT_ITEMS_MAX, true).forEach((t, i) => {
    const at = `targets[${i}]`;
    if (!isObj(t)) return c.fail(at, 'must be an object with id and label');
    const id = c.req(t, 'id', at, 100, true);
    if (id && !PROJECT_PROBLEM_ID_RE.test(id)) c.fail(`${at}.id`, `"${id}" may hold only A-Z, a-z, 0-9 and . _ : -`);
    else if (id && ids.has(id)) c.fail(`${at}.id`, `"${id}" is used twice`);
    ids.add(id);
    const target: DeploysFile['targets'][number] = { id, label: c.req(t, 'label', at) };
    const url = c.opt(t, 'url', at, 2000);
    if (url !== undefined) {
      if (/^https?:\/\/\S+$/i.test(url)) target.url = url;
      else c.note(`${at}.url`, 'must be an http or https address, left out');
    }
    const state = c.oneOf(t, 'state', at, ['idle', 'deploying'] as const, false);
    if (state) target.state = state;
    const phase = c.opt(t, 'phase', at, PROJECT_PHASE_MAX);
    if (phase) target.phase = phase;
    if (isObj(t.live)) {
      const version = c.req(t.live, 'version', `${at}.live`, 200, true);
      const liveAt = c.iso(t.live, 'at', `${at}.live`);
      const live: NonNullable<DeploysFile['targets'][number]['live']> = liveAt !== undefined ? { version, at: liveAt } : { version };
      const source = t.live.source;
      if (source !== undefined && source !== null) {
        // A value from a later version reads as unknown (v1 grows only by optional fields).
        if (typeof source === 'string' && (DEPLOY_SOURCES as readonly string[]).includes(source)) live.source = source as DeploySource;
        else c.note(`${at}.live.source`, `must be one of ${DEPLOY_SOURCES.join(', ')}, read as unknown`);
      }
      target.live = live;
    } else if (t.live !== undefined && t.live !== null) c.fail(`${at}.live`, 'must be an object with version');
    if (isObj(t.main)) target.main = { version: c.req(t.main, 'version', `${at}.main`, 200, true) };
    else if (t.main !== undefined && t.main !== null) c.fail(`${at}.main`, 'must be an object with version');
    if (isObj(t.last)) {
      const w = `${at}.last`;
      const last: NonNullable<DeploysFile['targets'][number]['last']> = { result: c.oneOf(t.last, 'result', w, DEPLOY_RESULTS, true) ?? 'failed' };
      const version = c.opt(t.last, 'version', w, 200);
      if (version !== undefined) last.version = version;
      const lastAt = c.iso(t.last, 'at', w);
      if (lastAt !== undefined) last.at = lastAt;
      const log = c.opt(t.last, 'log', w, 1000);
      if (log !== undefined) last.log = log;
      const message = c.opt(t.last, 'message', w, DEPLOY_MESSAGE_MAX);
      if (message !== undefined) last.message = message;
      target.last = last;
    } else if (t.last !== undefined && t.last !== null) c.fail(`${at}.last`, 'must be an object with result');
    file.targets.push(target);
  });
  return done(c, file);
}

/** Parse any of the three by kind. */
export function parseStateFile(kind: 'pipeline', text: string): StateFileResult<PipelineFile>;
export function parseStateFile(kind: 'health', text: string): StateFileResult<HealthFile>;
export function parseStateFile(kind: 'deploys', text: string): StateFileResult<DeploysFile>;
export function parseStateFile(kind: ProjectStateFileKind, text: string): StateFileResult<PipelineFile | HealthFile | DeploysFile>;
export function parseStateFile(kind: ProjectStateFileKind, text: string): StateFileResult<PipelineFile | HealthFile | DeploysFile> {
  return kind === 'pipeline' ? parsePipelineFile(text) : kind === 'health' ? parseHealthFile(text) : parseDeploysFile(text);
}

// ── Reporting ─────────────────────────────────────────────────────────────────

/** What an agent found for one state file: null when it is missing, else the parse result and the file's mtime. */
export type StateFileRead<F> = { result: StateFileResult<F>; mtimeMs?: number } | null;

/** The interval a file is held to: its own, else the manifest's, else the default. */
export function stateIntervalS(kind: ProjectStateFileKind, file: { interval?: number } | null | undefined, manifest: ProjectManifest | null | undefined): number {
  return file?.interval ?? manifest?.[kind]?.intervalS ?? PROJECT_DEFAULT_INTERVAL_S[kind];
}

/**
 * When the file was last written, for the 3 x interval rule: its updatedAt, except that a time
 * more than 5 minutes ahead counts as now. "Now" is when the file changed (its mtime) when the
 * agent knows it, so a writer with a fast clock that stops writing still goes stale.
 */
function effectiveUpdatedAt(updatedAt: Ts, now: Ts, mtimeMs?: number): Ts {
  if (updatedAt <= now + PROJECT_CLOCK_SKEW_MS) return updatedAt;
  return Math.min(now, mtimeMs ?? now);
}

/** How one state file stands (spec section 4). */
export function stateFileStatus(
  kind: ProjectStateFileKind,
  read: StateFileRead<{ updatedAt: string; interval?: number }>,
  manifest: ProjectManifest | null | undefined,
  now: Ts,
  /** The project's pipeline runner stopped on purpose (its pipeline.json is idle): nothing goes stale. */
  opts: { runnerResting?: boolean } = {},
): StateFileStatus {
  const name = `${kind}.json`;
  if (!read) {
    const intervalS = stateIntervalS(kind, null, manifest);
    if (!manifest?.[kind]) return { kind, state: 'absent', updatedAt: null, intervalS };
    return { kind, state: 'not-reporting', updatedAt: null, intervalS, reason: `orgus.yml declares ${kind === 'deploys' ? 'deploys' : `a ${kind}`}, but there is no ${name}` };
  }
  if ('error' in read.result) return { kind, state: 'not-reporting', updatedAt: null, intervalS: stateIntervalS(kind, null, manifest), reason: read.result.error };
  const file = read.result.file;
  const intervalS = stateIntervalS(kind, file, manifest);
  const updatedAt = effectiveUpdatedAt(parseIsoTs(file.updatedAt) ?? 0, now, read.mtimeMs);
  if (now - updatedAt > PROJECT_STALE_FACTOR * intervalS * 1000) {
    // A runner that wrote idle or paused may exit until there is work (spec section 4): its
    // files keep their last word instead of turning into a fault.
    const own = (file as { state?: unknown }).state;
    if (opts.runnerResting || (kind === 'pipeline' && (own === 'idle' || own === 'paused'))) {
      return { kind, state: 'idle', updatedAt, intervalS, reason: own === 'paused' ? 'paused, the runner is stopped' : 'the runner stopped with nothing to do' };
    }
    return { kind, state: 'not-reporting', updatedAt, intervalS, reason: `Older than ${PROJECT_STALE_FACTOR} x ${intervalS} s` };
  }
  return { kind, state: 'reporting', updatedAt, intervalS };
}

const isReporting = (s: StateFileStatus | null | undefined): boolean => s?.state === 'reporting';
const tsOf = (v: string | null | undefined): Ts | undefined => parseIsoTs(v) ?? undefined;

/** The pipeline buttons the manifest binds (pause, resume, restart). */
export function pipelineActionKinds(manifest: ProjectManifest | null | undefined): ActionKind[] {
  return PROJECT_ACTION_ROLES.filter((r) => !!actionForRole(manifest, r));
}

/** The log ref of a deploy target's last deploy (the adapter's logFile resolves it). */
export const deployLogRef = (targetId: string): string => `deploy:${targetId}`;

/**
 * pipeline.json onto the core Pipeline shape (adapter 'orgus-protocol'). Without a usable file
 * (missing, broken, stale) the pipeline is `down` and says why; the last good items stay
 * visible. Queued and active items are `items`; the newest 20 finished are `recent`. The
 * buttons are the roles the manifest declares. machineId is stamped by the hub.
 */
export function toPipeline(input: {
  projectId: Id;
  machineId?: Id;
  file: PipelineFile | null;
  status: StateFileStatus;
  manifest: ProjectManifest | null | undefined;
  now: Ts;
}): Pipeline {
  const { file, status, manifest } = input;
  const live = isReporting(status) && !!file;
  const toItem = (it: PipelineFile['items'][number]): PipelineItem => {
    const out: PipelineItem = { id: it.id, title: it.title, stage: it.stage, state: it.state };
    // The core item has no phase: it reads after the detail.
    const detail = it.phase ? (it.detail ? `${it.detail} (${it.phase})` : it.phase) : it.detail;
    if (detail) out.detail = detail;
    if (it.priority) out.priority = true;
    if (it.author) out.by = it.author;
    const startedAt = tsOf(it.startedAt);
    if (startedAt !== undefined) out.startedAt = startedAt;
    const stageStartedAt = tsOf(it.stageStartedAt);
    if (stageStartedAt !== undefined) out.stageStartedAt = stageStartedAt;
    const endedAt = tsOf(it.endedAt);
    if (endedAt !== undefined) out.endedAt = endedAt;
    if (it.log) out.logRef = it.id;
    if (it.tags?.length) out.tags = [...it.tags];
    if (it.scope) out.scope = { files: it.scope.files, units: [...it.scope.units], ...(it.scope.docsOnly ? { docsOnly: true } : {}) };
    return out;
  };
  const items = file?.items ?? [];
  const open = items.filter((it) => it.state === 'queued' || it.state === 'active');
  const finished = items
    .map((it, i) => ({ it, i, at: tsOf(it.endedAt) ?? tsOf(it.stageStartedAt) ?? tsOf(it.startedAt) ?? -1 }))
    .filter((x) => x.it.state !== 'queued' && x.it.state !== 'active')
    .sort((a, b) => b.at - a.at || b.i - a.i)
    .slice(0, RECENT_MAX);
  const pipeline: Pipeline = {
    projectId: input.projectId,
    machineId: input.machineId ?? '',
    adapter: PROJECT_PROTOCOL_ADAPTER,
    title: file?.title || manifest?.pipeline?.title || 'Pipeline',
    state: live || (status.state === 'idle' && file) ? file!.state : 'down',
    stages: (file?.stages ?? []).map((s) => ({ id: s.id, title: s.title })),
    items: open.map(toItem),
    recent: finished.map((x) => toItem(x.it)),
    actions: pipelineActionKinds(manifest),
    updatedAt: status.updatedAt ?? input.now,
  };
  if (!live && status.state !== 'idle') pipeline.error = `Not reporting: ${status.reason ?? 'there is no pipeline.json'}`;
  else if (file?.state === 'error') pipeline.error = file.error || 'The pipeline reports an error but not what failed.';
  if (live && file?.hold) pipeline.hold = file.hold;
  if (file?.batch) pipeline.batch = file.batch.unit ? { size: file.batch.size, unit: file.batch.unit } : { size: file.batch.size };
  if (file?.capacity) {
    const k = file.capacity;
    const claim = (h: PipelineFileClaim): PipelineCapacityClaim => {
      const out: PipelineCapacityClaim = { label: h.label, slots: h.slots };
      if (h.pid !== undefined) out.pid = h.pid;
      if (h.run !== undefined) out.run = h.run;
      const since = tsOf(h.since);
      if (since !== undefined) out.since = since;
      if (h.reserved) out.reserved = true;
      return out;
    };
    const cap: PipelineCapacity = { label: k.label, used: k.used, total: k.total, waiting: k.waiting };
    if (k.holders) cap.holders = k.holders.map(claim);
    if (k.queue) cap.queue = k.queue.map(claim);
    if (k.runs !== undefined) cap.runs = k.runs;
    if (k.runLabel !== undefined) cap.runLabel = k.runLabel;
    if (k.reserve) cap.reserve = { ...k.reserve };
    pipeline.capacity = cap;
  }
  return pipeline;
}

const counted = (n: number, unit: string): string => `${n} ${unit}${n === 1 || unit.endsWith('s') ? '' : 's'}`;

/**
 * Landings and deploys in progress (spec section 6), from reporting files only: active items in
 * a `landing` or `deploy` stage, and deploy targets with state `deploying`.
 */
export function busyFrom(input: {
  projectId: Id;
  machineId?: Id;
  pipeline: { file: PipelineFile | null; status: StateFileStatus } | null;
  deploys: { file: DeploysFile | null; status: StateFileStatus } | null;
}): ProjectBusy[] {
  const out: ProjectBusy[] = [];
  const base = { projectId: input.projectId, machineId: input.machineId ?? '' };
  const p = input.pipeline;
  if (p?.file && isReporting(p.status)) {
    const f = p.file;
    const kindOf = new Map(f.stages.map((s) => [s.id, s.kind]));
    const updated = parseIsoTs(f.updatedAt) ?? 0;
    for (const kind of ['landing', 'deploy'] as const) {
      const active = f.items.filter((it) => it.state === 'active' && kindOf.get(it.stage) === kind);
      if (!active.length) continue;
      const since = Math.min(...active.map((it) => tsOf(it.stageStartedAt) ?? tsOf(it.startedAt) ?? updated));
      const unit = f.batch?.unit ?? 'item';
      const what =
        kind === 'landing'
          ? `landing ${counted(f.batch?.size || active.length, unit)}`
          : active.length === 1
            ? `deploying ${active[0]?.title ?? ''}`
            : `deploying ${counted(active.length, unit)}`;
      out.push({ ...base, kind, what, since });
    }
  }
  const d = input.deploys;
  if (d?.file && isReporting(d.status)) {
    const updated = parseIsoTs(d.file.updatedAt) ?? 0;
    for (const t of d.file.targets) {
      if (t.state !== 'deploying') continue;
      const since = (t.last?.result === 'running' ? tsOf(t.last.at) : undefined) ?? updated;
      out.push({ ...base, kind: 'deploy', what: `deploying ${t.label}`, since });
    }
  }
  return out;
}

const SEVERITY_ORDER: Record<ProblemSeverity, number> = { critical: 0, warning: 1, info: 2 };

/**
 * health.json's problems (source 'project', key `project:<projectId>:<id>`), most severe first;
 * none when the file is not reporting. `actionId` only for an action the manifest declares.
 */
export function healthProblems(input: {
  projectId: Id;
  machineId?: Id | null;
  file: HealthFile | null;
  status: StateFileStatus;
  manifest?: ProjectManifest | null;
}): Problem[] {
  const { file } = input;
  if (!file || !isReporting(input.status)) return [];
  const updated = parseIsoTs(file.updatedAt) ?? 0;
  return file.problems
    .map((p) => {
      const out: Problem = {
        key: `project:${input.projectId}:${p.id}`,
        id: p.id,
        source: 'project',
        severity: p.severity,
        what: p.what,
        why: p.why,
        next: p.next,
        projectId: input.projectId,
        machineId: input.machineId ?? null,
        since: tsOf(p.since) ?? updated,
      };
      if (p.action && declaredAction(input.manifest, p.action)) out.actionId = p.action;
      return out;
    })
    .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] || a.since - b.since);
}

/** deploys.json's targets. The last known versions stay visible; a target counts as deploying only while the file reports. */
export function deployTargets(file: DeploysFile | null, status: StateFileStatus): DeployTarget[] {
  if (!file) return [];
  const live = isReporting(status);
  return file.targets.map((t) => {
    const liveV = t.live ? { version: t.live.version, at: tsOf(t.live.at) ?? null, source: t.live.source ?? 'unknown' } : null;
    const mainVersion = t.main?.version ?? null;
    return {
      id: t.id,
      label: t.label,
      url: t.url ?? null,
      deploying: live && t.state === 'deploying',
      ...(t.phase ? { phase: t.phase } : {}),
      live: liveV,
      mainVersion,
      behind: !!liveV && mainVersion !== null && liveV.version !== mainVersion,
      last: t.last ? { result: t.last.result, version: t.last.version ?? null, at: tsOf(t.last.at) ?? null, logRef: t.last.log ? deployLogRef(t.id) : null, ...(t.last.message ? { message: t.last.message } : {}) } : null,
    };
  });
}

/** Everything an agent reports for one project, from what it read. */
export function projectReport(input: {
  projectId: Id;
  machineId?: Id;
  manifest: ProjectManifest | null;
  manifestError?: string;
  reads: { pipeline: StateFileRead<PipelineFile>; health: StateFileRead<HealthFile>; deploys: StateFileRead<DeploysFile> };
  pipelineSource: ProjectProtocolReport['pipelineSource'];
  now: Ts;
}): ProjectProtocolReport {
  const { manifest, reads, now, projectId } = input;
  const fileOf = <F>(r: StateFileRead<F>): F | null => (r && 'file' in r.result ? r.result.file : null);
  const pipelineStatus = stateFileStatus('pipeline', reads.pipeline, manifest, now);
  // One runner usually writes all three files: when it rests, none of them is late.
  const resting = { runnerResting: pipelineStatus.state === 'idle' };
  const healthStatus = stateFileStatus('health', reads.health, manifest, now, resting);
  const deploysStatus = stateFileStatus('deploys', reads.deploys, manifest, now, resting);
  const machine = input.machineId !== undefined ? { machineId: input.machineId } : {};
  const report: ProjectProtocolReport = {
    projectId,
    manifest,
    files: [pipelineStatus, healthStatus, deploysStatus],
    problems: healthProblems({ projectId, ...machine, file: fileOf(reads.health), status: healthStatus, manifest }),
    deploys: deployTargets(fileOf(reads.deploys), deploysStatus),
    busy: busyFrom({ projectId, ...machine, pipeline: { file: fileOf(reads.pipeline), status: pipelineStatus }, deploys: { file: fileOf(reads.deploys), status: deploysStatus } }),
    pipelineSource: input.pipelineSource,
    readAt: now,
  };
  if (input.manifestError) report.manifestError = input.manifestError;
  return report;
}
